/**
 * Modal de Busca Avançada
 * Combina SearchBar, SearchFilters e SearchResults em uma interface completa
 */

import React, { useState, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  Box,
  IconButton,
  Typography,
  Drawer,
  Divider,
  Button,
  useMediaQuery,
  useTheme,
  alpha,
  Chip
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import FilterListIcon from '@mui/icons-material/FilterList'
import TuneIcon from '@mui/icons-material/Tune'
import SearchIcon from '@mui/icons-material/Search'
import SearchBar from './SearchBar'
import SearchFilters from './SearchFilters'
import SearchResults from './SearchResults'
import { useSearch } from '../hooks/useSearch'
import type { FileItem } from '../../../services/api'
import type { Transcription, Entity, AISummary } from '../../../types/transcription'
import type { TranscriptionFolder } from '../../folders/types/folder.types'

const SENTIMENT_LABELS: Record<string, string> = {
  positive: 'Positivo',
  neutral: 'Neutro',
  negative: 'Negativo',
}

const ENTITY_TYPE_LABELS: Record<string, string> = {
  PERSON: 'Pessoa',
  ORGANIZATION: 'Organização',
  LOCATION: 'Local',
  DATE: 'Data',
  TIME: 'Horário',
  MONEY: 'Valor',
  LEGAL_TERM: 'Termo Jurídico',
  EVIDENCE: 'Evidência',
}

interface SearchModalProps {
  /** Controla se o modal está aberto */
  open: boolean
  /** Callback para fechar o modal */
  onClose: () => void
  /** Lista de arquivos disponíveis */
  files: FileItem[]
  /** Pastas disponíveis */
  folders?: TranscriptionFolder[]
  /** Callback para abrir um arquivo */
  onOpenFile: (fileId: string, segmentIndex?: number) => void
  /** Callback para obter transcrição de um arquivo */
  getTranscription?: (fileId: string) => Promise<Transcription | null>
  /** Callback para obter entidades de um arquivo */
  getEntities?: (fileId: string) => Promise<Entity[] | null>
  /** Callback para obter resumo de um arquivo */
  getSummary?: (fileId: string) => Promise<AISummary | null>
}

export default function SearchModal({
  open,
  onClose,
  files,
  folders = [],
  onOpenFile,
  getTranscription,
  getEntities,
  getSummary
}: SearchModalProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [showFilters, setShowFilters] = useState(!isMobile)

  // Hook de busca
  const {
    query,
    filters,
    scope,
    results,
    isSearching,
    error,
    activeCategory,
    suggestions,
    availableSpeakers,
    setQuery,
    setFilters,
    setScope,
    setActiveCategory,
    search,
    clearSearch
  } = useSearch({
    files,
    getTranscription,
    getEntities,
    getSummary
  })

  // Handler para abrir arquivo e fechar modal
  const handleOpenFile = useCallback((fileId: string, segmentIndex?: number) => {
    onOpenFile(fileId, segmentIndex)
    onClose()
  }, [onOpenFile, onClose])

  // Conta filtros ativos
  const activeFiltersCount =
    (filters.speakers?.length || 0) +
    (filters.sentiment?.length || 0) +
    (filters.entityTypes?.length || 0) +
    (filters.dateRange?.from ? 1 : 0) +
    (filters.dateRange?.to ? 1 : 0) +
    (filters.hasAnnotations ? 1 : 0) +
    (filters.legalTermsOnly ? 1 : 0)

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: '#f8f9fa'
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 2 },
          px: { xs: 1.5, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          bgcolor: '#fff',
          borderBottom: '1px solid #e0e0e0',
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}
      >
        <SearchIcon sx={{ fontSize: { xs: 24, sm: 28 }, color: '#1976d2' }} />
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Busca Avançada
        </Typography>

        <Box sx={{ flex: 1 }} />

        {/* Toggle filtros (mobile) */}
        {isMobile && (
          <Button
            variant={showFilters ? 'contained' : 'outlined'}
            size="small"
            startIcon={<TuneIcon />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{ borderRadius: 2 }}
          >
            Filtros
            {activeFiltersCount > 0 && (
              <Chip
                label={activeFiltersCount}
                size="small"
                sx={{
                  ml: 0.5,
                  height: 18,
                  fontSize: '0.7rem',
                  bgcolor: showFilters ? alpha('#fff', 0.2) : '#1976d2',
                  color: showFilters ? '#fff' : '#fff'
                }}
              />
            )}
          </Button>
        )}

        <IconButton onClick={onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, display: 'flex', overflow: 'hidden' }}>
        {/* Sidebar de filtros (desktop) ou Drawer (mobile) */}
        {isMobile ? (
          <Drawer
            anchor="left"
            open={showFilters}
            onClose={() => setShowFilters(false)}
            ModalProps={{
              // Z-index acima do Dialog fullScreen (1300)
              sx: { zIndex: 1400 }
            }}
            PaperProps={{
              sx: { width: { xs: '85%', sm: 320 }, maxWidth: 360, p: 2 }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <FilterListIcon sx={{ mr: 1 }} />
              <Typography variant="subtitle1" fontWeight={600}>
                Filtros de Busca
              </Typography>
              <Box sx={{ flex: 1 }} />
              <IconButton size="small" onClick={() => setShowFilters(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <SearchFilters
              filters={filters}
              scope={scope}
              onFiltersChange={setFilters}
              onScopeChange={setScope}
              availableSpeakers={availableSpeakers}
              availableFolders={folders}
              compact
              onApply={() => {
                search()
                setShowFilters(false)
              }}
            />
          </Drawer>
        ) : (
          <Box
            sx={{
              width: 320,
              flexShrink: 0,
              borderRight: '1px solid #e0e0e0',
              bgcolor: '#fff',
              overflowY: 'auto',
              p: 2
            }}
          >
            <SearchFilters
              filters={filters}
              scope={scope}
              onFiltersChange={setFilters}
              onScopeChange={setScope}
              availableSpeakers={availableSpeakers}
              availableFolders={folders}
            />
          </Box>
        )}

        {/* Área principal */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          {/* Search bar */}
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              bgcolor: '#fff',
              borderBottom: '1px solid #e0e0e0'
            }}
          >
            <SearchBar
              value={query}
              onChange={setQuery}
              onSearch={search}
              suggestions={suggestions}
              isSearching={isSearching}
              placeholder="Buscar em transcrições, entidades, análises..."
              showFiltersButton={isMobile}
              onOpenFilters={() => setShowFilters(true)}
              autoFocus
            />

            {/* Quick filters tags */}
            {activeFiltersCount > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 1, alignSelf: 'center' }}>
                  Filtros ativos:
                </Typography>
                {filters.speakers?.map(speaker => (
                  <Chip
                    key={speaker}
                    label={`Falante: ${speaker}`}
                    size="small"
                    onDelete={() => setFilters({
                      speakers: filters.speakers?.filter(s => s !== speaker)
                    })}
                    sx={{ height: 24 }}
                  />
                ))}
                {filters.sentiment?.map(s => (
                  <Chip
                    key={s}
                    label={`Sentimento: ${SENTIMENT_LABELS[s] ?? s}`}
                    size="small"
                    onDelete={() => setFilters({
                      sentiment: filters.sentiment?.filter(x => x !== s)
                    })}
                    sx={{ height: 24 }}
                  />
                ))}
                {filters.entityTypes?.map(t => (
                  <Chip
                    key={t}
                    label={`Entidade: ${ENTITY_TYPE_LABELS[t] ?? t}`}
                    size="small"
                    onDelete={() => setFilters({
                      entityTypes: filters.entityTypes?.filter(x => x !== t)
                    })}
                    sx={{ height: 24 }}
                  />
                ))}
                {filters.hasAnnotations && (
                  <Chip
                    label="Com anotações"
                    size="small"
                    onDelete={() => setFilters({ hasAnnotations: undefined })}
                    sx={{ height: 24 }}
                  />
                )}
                {filters.legalTermsOnly && (
                  <Chip
                    label="Termos jurídicos"
                    size="small"
                    onDelete={() => setFilters({ legalTermsOnly: undefined })}
                    sx={{ height: 24 }}
                  />
                )}
              </Box>
            )}
          </Box>

          {/* Results */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: { xs: 1.5, sm: 2 }
            }}
          >
            {/* Error state */}
            {error && (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="error" gutterBottom>
                  {error}
                </Typography>
                <Button variant="outlined" onClick={search}>
                  Tentar novamente
                </Button>
              </Box>
            )}

            {/* No query state */}
            {!query && !results && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: { xs: 200, sm: 300 },
                  color: 'text.secondary',
                  px: { xs: 2, sm: 0 }
                }}
              >
                <SearchIcon sx={{ fontSize: { xs: 48, sm: 64 }, mb: 2, opacity: 0.3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, textAlign: 'center' }}>
                  Busque em todas as transcrições
                </Typography>
                <Typography variant="body2" sx={{ maxWidth: 400, textAlign: 'center', px: { xs: 1, sm: 0 } }}>
                  Encontre trechos específicos, entidades mencionadas, análises de sentimento
                  e muito mais em todos os seus arquivos transcritos.
                </Typography>

                <Box sx={{ mt: { xs: 3, sm: 4 } }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ textAlign: 'center' }}>
                    Dicas de busca:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, mt: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Chip
                      label='falante:"João Silva"'
                      variant="outlined"
                      size="small"
                      onClick={() => setQuery('falante:"João Silva"')}
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}
                    />
                    <Chip
                      label='"frase exata"'
                      variant="outlined"
                      size="small"
                      onClick={() => setQuery('"frase exata"')}
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}
                    />
                    <Chip
                      label='termo -excluir'
                      variant="outlined"
                      size="small"
                      onClick={() => setQuery('termo -excluir')}
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}
                    />
                  </Box>
                </Box>
              </Box>
            )}

            {/* Results */}
            {(results || isSearching) && (
              <SearchResults
                results={results}
                activeCategory={activeCategory}
                onCategoryChange={setActiveCategory}
                onOpenFile={handleOpenFile}
                isLoading={isSearching}
                query={query}
              />
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
